import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

/**
 * Dispatches scroll and resize events to trigger layout recalculations.
 */
const dispatchWindowEvents = (): void => {
  window.dispatchEvent(new Event('scroll'));
  window.dispatchEvent(new Event('resize'));
};

/**
 * Automatically collapses the application sidebar on component mount and
 * restores it on unmount.
 *
 * This hook is designed for full-width views like data tables where the
 * sidebar would unnecessarily constrain the available screen space. The
 * collapse happens unconditionally to maximize the viewing area.
 *
 * @example
 * ```tsx
 * const DataTableView: FC = () => {
 *   useSidebarAutoCollapse();
 *   return <DataTable />;
 * };
 * ```
 */
export const useSidebarAutoCollapse = (): void => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: 'DISPLAY_OUTER_SIDEBAR', data: { display: false } });
    dispatchWindowEvents();

    return () => {
      dispatch({ type: 'DISPLAY_OUTER_SIDEBAR', data: { display: true } });
      dispatchWindowEvents();
    };
  }, [dispatch]);
};
